import { AdminDomainsSortField, AdminDomainsSortOrder, AdminDomainsTypeFilter } from '@@types/api.types';
import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { parseAsInteger, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORTING: SortingState = [{ desc: false, id: 'domain' }];

const COLUMN_ID_TO_SORT_FIELD: Record<string, AdminDomainsSortField> = {
  domain: AdminDomainsSortField.HIERARCHY,
  name: AdminDomainsSortField.NAME,
  parent: AdminDomainsSortField.PARENT,
  status: AdminDomainsSortField.STATUS,
};

type AdminDomainsQueryParams = {
  limit: number;
  order: AdminDomainsSortOrder;
  page: number;
  search?: string;
  sort: AdminDomainsSortField;
  type: AdminDomainsTypeFilter;
};

type UseAdminDomainsQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  handleLimitChange: (newLimit: number) => void;
  page: number;
  queryParams: AdminDomainsQueryParams;
  search: string;
  selectedDomainId: string | null;
  setDomainType: (type: AdminDomainsTypeFilter) => Promise<URLSearchParams>;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string) => Promise<URLSearchParams>;
  setSelectedDomainId: (domainId: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState) => Promise<URLSearchParams>;
  sortingState: SortingState;
  type: AdminDomainsTypeFilter;
};

export const useAdminDomainsQueryParams = (): UseAdminDomainsQueryParamsReturn => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_PAGE_SIZE));
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''));
  const [selectedDomainId, setSelectedDomainId] = useQueryState('domainId', parseAsString);
  const [type, setDomainType] = useQueryState(
    'type',
    parseAsStringEnum<AdminDomainsTypeFilter>([
      AdminDomainsTypeFilter.ALL,
      AdminDomainsTypeFilter.MAIN,
      AdminDomainsTypeFilter.SUB,
    ]).withDefault(AdminDomainsTypeFilter.ALL),
  );
  const [sorting, setSorting] = useQueryState('sorting', parseAsSortingState.withDefault(DEFAULT_SORTING));

  const sortingState: SortingState = sorting ?? DEFAULT_SORTING;

  const columnId = sortingState[0]?.id || 'name';
  const sortField = COLUMN_ID_TO_SORT_FIELD[columnId] || AdminDomainsSortField.NAME;
  const sortOrder = sortingState[0]?.desc ? AdminDomainsSortOrder.DESC : AdminDomainsSortOrder.ASC;

  const queryParams = useMemo(() => {
    return {
      limit: limit ?? DEFAULT_PAGE_SIZE,
      order: sortOrder,
      page: page ?? 1,
      search: search || undefined,
      sort: sortField,
      type: type ?? AdminDomainsTypeFilter.ALL,
    };
  }, [limit, page, search, sortField, sortOrder, type]);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      void setLimit(newLimit);
      void setPage(1);
    },
    [setLimit, setPage],
  );

  return {
    DEFAULT_SORTING,
    handleLimitChange,
    page: page ?? 1,
    queryParams,
    search: search ?? '',
    selectedDomainId,
    setDomainType,
    setPage,
    setSearch,
    setSelectedDomainId,
    setSorting,
    sortingState,
    type: type ?? AdminDomainsTypeFilter.ALL,
  };
};
